import { cva } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import { clsxm } from '@vs-shop/utils';

import { optionalClassName } from '../../internal/optionalClassName.ts';

/**
 * The catalogue drawer behind a storefront's hamburger: the bar across its top,
 * the level a shopper is standing on, and the rows that level is made of.
 *
 *   <NavigationDrawerBar>
 *     <NavigationDrawerBarLead>{canGoBack ? <BackControl /> : null}</NavigationDrawerBarLead>
 *     <NavigationDrawerBarTitle>
 *       {title === null ? null : <NavigationDrawerBarLabel>{title}</NavigationDrawerBarLabel>}
 *     </NavigationDrawerBarTitle>
 *     <DialogClose asChild><CloseControl /></DialogClose>
 *   </NavigationDrawerBar>
 *
 *   <NavigationDrawerLevel>
 *     <NavigationDrawerList>
 *       <NavigationDrawerItem>
 *         <NavigationDrawerRow variant="standard" onClick={…}>
 *           <NavigationDrawerRowMain>
 *             <NavigationDrawerRowFigure variant="muted"><Image … /></NavigationDrawerRowFigure>
 *             <NavigationDrawerRowLabel>{taxon.name}</NavigationDrawerRowLabel>
 *           </NavigationDrawerRowMain>
 *           <NavigationDrawerRowMark variant="static"><ChevronRight /></NavigationDrawerRowMark>
 *         </NavigationDrawerRow>
 *       </NavigationDrawerItem>
 *     </NavigationDrawerList>
 *   </NavigationDrawerLevel>
 *
 * **The dialog underneath is the tenant's.** What opens the drawer, what it
 * portals into, what closes it and what returns focus afterwards are the host's
 * dialog library's answers, and this section is only what the panel holds — so
 * the bar takes the close affordance as a child rather than reaching for a
 * dialog primitive of its own.
 *
 * **One row, worn three ways.** A catalogue taxon, a way to the whole of the
 * level above it, and a shop's own announcement are the same row: a full-width
 * left-aligned target with an optional leading frame, a clamped label and an
 * optional trailing mark. Which of the three parts are present is composition;
 * no prop decides it, and a row with no frame does not inherit the label's
 * position from the frame's absence — the main group anchors in its own recipe.
 *
 * **The drill-down itself is not here.** Which level is showing, what its title
 * is, what a row does when pressed and what is fetched to answer it are the
 * tenant's state and the tenant's data. Every part is a plain component, and
 * the tenant's client composition drives them.
 */

/* ── the bar ──────────────────────────────────────────────────────────── */

/**
 * The bar across the top of the panel: a way back at one end, a way out at the
 * other, what the shopper is looking at between them.
 *
 * The three places are held rather than packed, so the title stays centred
 * whether or not there is anywhere to go back to.
 */
const navigationDrawerBarClasses = 'flex h-10 w-full items-center justify-between gap-3';

export type NavigationDrawerBarProps = ComponentProps<'div'>;

/** The drawer's top bar. */
export const NavigationDrawerBar = ({ className, ...props }: NavigationDrawerBarProps) => (
  <div
    data-slot="navigation-drawer-bar"
    className={clsxm(navigationDrawerBarClasses, className)}
    {...props}
  />
);

export type NavigationDrawerBarLeadProps = ComponentProps<'div'>;

/**
 * The leading place of the bar, where a way back appears once there is one.
 *
 * It paints nothing and it is always rendered: an empty place is what keeps the
 * title where it was on the level above, rather than letting it slide left the
 * moment the back control is gone.
 */
export const NavigationDrawerBarLead = ({ className, ...props }: NavigationDrawerBarLeadProps) => (
  <div
    data-slot="navigation-drawer-bar-lead"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

export type NavigationDrawerBarTitleProps = ComponentProps<'div'>;

/**
 * The middle place of the bar. Always rendered for the same reason the leading
 * one is — the top level has no title, and the way out must not move.
 */
export const NavigationDrawerBarTitle = ({
  className,
  ...props
}: NavigationDrawerBarTitleProps) => (
  <div
    data-slot="navigation-drawer-bar-title"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/**
 * What the level is called.
 *
 * A neutral element rather than a heading: the drawer is chrome a shopper can
 * open on any route, so a heading inside it would compete with that page's own
 * outline. The dialog's accessible name is the tenant's dialog title.
 */
const navigationDrawerBarLabelClasses = 'line-clamp-1 text-xl';

export type NavigationDrawerBarLabelProps = ComponentProps<'span'>;

/** The level's name. */
export const NavigationDrawerBarLabel = ({
  className,
  ...props
}: NavigationDrawerBarLabelProps) => (
  <span
    data-slot="navigation-drawer-bar-label"
    className={clsxm(navigationDrawerBarLabelClasses, className)}
    {...props}
  />
);

/* ── the level ────────────────────────────────────────────────────────── */

export type NavigationDrawerLevelProps = ComponentProps<'div'>;

/**
 * One rung of the drill-down.
 *
 * It paints nothing: the panel's own scrolling and padding belong to the tenant
 * composition around it, and the rules between rows belong to the list. What
 * this owns is the boundary — a level is what gets replaced when a shopper goes
 * one deeper, so it has to be an element a composition can name.
 */
export const NavigationDrawerLevel = ({ className, ...props }: NavigationDrawerLevelProps) => (
  <div
    data-slot="navigation-drawer-level"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/** The level's rows, ruled between rather than around. */
const navigationDrawerListClasses = 'divide-background-intense-more divide-y';

export type NavigationDrawerListProps = ComponentProps<'ul'>;

/** The list of rows. */
export const NavigationDrawerList = ({ className, ...props }: NavigationDrawerListProps) => (
  <ul
    data-slot="navigation-drawer-list"
    className={clsxm(navigationDrawerListClasses, className)}
    {...props}
  />
);

export type NavigationDrawerItemProps = ComponentProps<'li'>;

/**
 * One row's place in the list.
 *
 * Paints nothing — the rule above it is the list's and the look is the row's —
 * and exists so the anatomy is reachable and the list stays a list.
 */
export const NavigationDrawerItem = ({ className, ...props }: NavigationDrawerItemProps) => (
  <li
    data-slot="navigation-drawer-item"
    className={optionalClassName(clsxm(className))}
    {...props}
  />
);

/* ── the row ──────────────────────────────────────────────────────────── */

/**
 * The row itself, and the only interactive element of it.
 *
 * A `<button>` rather than a link: pressing a row in a drill-down opens the
 * level below it far more often than it navigates, and the tenant that does
 * navigate does so from the handler. A row that must be an anchor paints
 * through `render` — which no tenant needs yet, so the escape is not offered
 * until one does.
 */
export const navigationDrawerRowVariants = cva(
  'flex w-full cursor-pointer items-center justify-between gap-3 py-3 text-left disabled:cursor-default',
  {
    variants: {
      variant: {
        /** A way further into the catalogue. */
        standard: '',
        /**
         * A row that is announcing something rather than leading deeper. The
         * colour is set here and inherited by what the row holds, so a label and
         * a mark cannot drift apart.
         */
        warning: 'text-warning',
      },
    },
  }
);

/** Required and closed — not `VariantProps`, which makes it optional. */
export type NavigationDrawerRowVariantProps = { readonly variant: 'standard' | 'warning' };

export type NavigationDrawerRowProps = ComponentProps<'button'> & NavigationDrawerRowVariantProps;

/** One row of a level. */
export const NavigationDrawerRow = ({ className, variant, ...props }: NavigationDrawerRowProps) => (
  <button
    type="button"
    data-slot="navigation-drawer-row"
    className={clsxm(navigationDrawerRowVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The row's leading group: whatever frame it has, and what it is called.
 *
 * This is where the label is anchored — at the group's own leading edge — which
 * is what makes a frame optional: a row with nothing to show composes no frame,
 * and the label starts where the frame would have, rather than being pushed
 * around by how many children the row happens to hold.
 */
const navigationDrawerRowMainClasses = 'flex items-center gap-3';

export type NavigationDrawerRowMainProps = ComponentProps<'div'>;

/** The row's leading group. */
export const NavigationDrawerRowMain = ({ className, ...props }: NavigationDrawerRowMainProps) => (
  <div
    data-slot="navigation-drawer-row-main"
    className={clsxm(navigationDrawerRowMainClasses, className)}
    {...props}
  />
);

/**
 * The row's leading frame, and how big whatever hangs in it reads.
 *
 * The mark is children — a taxon photograph in one shop, a drawing in another —
 * so the part fixes the box rather than taking either as a prop.
 *
 * It sizes an IMAGE and not a drawing, and the asymmetry is deliberate: a
 * photograph has to fill this frame or it is not framed at all, while a drawing
 * is centred in it at whatever size it was drawn — a catalogue's stand-in mark
 * and a shop's own glyph are legible at different sizes in the same box. The
 * frame states the box; the tenant states the drawing.
 */
export const navigationDrawerRowFigureVariants = cva(
  'flex h-[36px] w-[46px] shrink-0 items-center justify-center rounded-sm border [&_img]:h-full [&_img]:w-full [&_img]:object-contain',
  {
    variants: {
      variant: {
        /** A frame that recedes: the catalogue's own rows. */
        muted: 'border-background-intense-more',
        /** A frame that is part of an announcement. */
        warning: 'border-warning/25',
      },
    },
  }
);

/** Required and closed — not `VariantProps`, which makes it optional. */
export type NavigationDrawerRowFigureVariantProps = { readonly variant: 'muted' | 'warning' };

export type NavigationDrawerRowFigureProps = ComponentProps<'div'> &
  NavigationDrawerRowFigureVariantProps;

/** The row's leading frame. */
export const NavigationDrawerRowFigure = ({
  className,
  variant,
  ...props
}: NavigationDrawerRowFigureProps) => (
  <div
    data-slot="navigation-drawer-row-figure"
    className={clsxm(navigationDrawerRowFigureVariants({ variant }), className)}
    {...props}
  />
);

/** What the row is called, clamped to the one line the measure holds. */
const navigationDrawerRowLabelClasses = 'line-clamp-1';

export type NavigationDrawerRowLabelProps = ComponentProps<'span'>;

/** The row's name. */
export const NavigationDrawerRowLabel = ({
  className,
  ...props
}: NavigationDrawerRowLabelProps) => (
  <span
    data-slot="navigation-drawer-row-label"
    className={clsxm(navigationDrawerRowLabelClasses, className)}
    {...props}
  />
);

/**
 * The row's trailing mark, and how it behaves while the row is answering.
 *
 * Which drawing hangs here is the tenant's — a chevron, a loader, a brand's own
 * glyph — so the part states the size over whatever is handed in and owns the
 * one thing that is appearance rather than artwork: whether the mark is turning.
 */
export const navigationDrawerRowMarkVariants = cva(
  'flex shrink-0 items-center [&_svg]:size-6 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        /** The row is at rest. */
        static: '',
        /** The row is waiting on an answer. */
        busy: '[&_svg]:animate-spin',
      },
    },
  }
);

/** Required and closed — not `VariantProps`, which makes it optional. */
export type NavigationDrawerRowMarkVariantProps = { readonly variant: 'static' | 'busy' };

export type NavigationDrawerRowMarkProps = ComponentProps<'div'> &
  NavigationDrawerRowMarkVariantProps;

/** The row's trailing mark. */
export const NavigationDrawerRowMark = ({
  className,
  variant,
  ...props
}: NavigationDrawerRowMarkProps) => (
  <div
    data-slot="navigation-drawer-row-mark"
    className={clsxm(navigationDrawerRowMarkVariants({ variant }), className)}
    {...props}
  />
);
